// Password reset, routed by params: ?reset_password=request shows the
// request form (POST auth/password/request); ?reset_password=<token> shows
// the completion form (POST auth/password/complete). Body shapes are the
// desk's ({email}, {token, password}); api-surface 2.0 lists only answers.
import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { postPasswordComplete, postPasswordRequest } from '../../api/endpoints';
import { isApiError } from '../../api/errors';
import { useElapsedLine } from '../../states/useElapsed';
import { HonestState } from '../../states/HonestState';
import { stateOfError } from '../../states/stateOf';
import { Button } from '../../ui/Button';
import { Field } from '../../ui/Input';
import { AuthFrame } from './AuthFrame';
import { PASSWORD_MIN_LENGTH } from './InvitationAccept';

export const RESET_REQUEST_MARKER = 'request';

export interface ResetPasswordProps {
  readonly param: string;
  readonly onDone: () => void;
}

export function isResetRequest(param: string): boolean {
  return param === RESET_REQUEST_MARKER;
}

function RequestForm({ onDone }: { readonly onDone: () => void }) {
  const [email, setEmail] = useState('');
  const request = useMutation({ mutationFn: postPasswordRequest });
  const elapsed = useElapsedLine(request.isPending);
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (email.trim()) request.mutate(email.trim());
  };
  return (
    <AuthFrame title="Reset your password" where="Password reset" lede="Enter the email on your account. If it matches a member, a reset link is sent; the answer is the same either way.">
      {request.isSuccess ? (
        <HonestState variant="Ready" title="Reset requested" reason="If that email belongs to a member, a reset link is on its way. It expires; open it soon." nextAction={{ label: 'Back to sign in', onSelect: onDone }} />
      ) : (
        <form className="door-form" onSubmit={onSubmit} aria-label="Request password reset">
          <label className="door-lbl" htmlFor="reset-email">Email</label>
          <Field id="reset-email" name="email" type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
          <div className="door-row">
            <Button variant="primary" type="submit" disabled={request.isPending}>{request.isPending ? 'Sending' : 'Send reset link'}</Button>
            <Button variant="tertiary" onClick={onDone}>Back to sign in</Button>
          </div>
          {request.isPending ? <HonestState variant="Loading" reason="Waiting for the reset answer (15 s limit)." detail={elapsed} nextAction={{ label: 'Cancel', onSelect: () => request.reset() }} /> : null}
          {request.isError ? <HonestState variant={stateOfError(request.error).variant} title="Reset request failed" reason={stateOfError(request.error).reason} nextAction={{ label: 'Try again', onSelect: () => request.reset() }} /> : null}
        </form>
      )}
    </AuthFrame>
  );
}

function CompleteForm({ token, onDone }: { readonly token: string; readonly onDone: () => void }) {
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const complete = useMutation({ mutationFn: (next: string) => postPasswordComplete(token, next) });
  const elapsed = useElapsedLine(complete.isPending);
  const tooShort = password.length > 0 && password.length < PASSWORD_MIN_LENGTH;
  const mismatch = again.length > 0 && again !== password;
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (password.length < PASSWORD_MIN_LENGTH || password !== again) return;
    complete.mutate(password);
  };
  const rejected = complete.isError && isApiError(complete.error) && (complete.error.status === 400 || complete.error.status === 404 || complete.error.status === 410);
  return (
    <AuthFrame title="Choose a new password" where="Password reset">
      {complete.isSuccess ? (
        <HonestState variant="Ready" title="Password replaced" reason="Sign in with the new password. Other sessions may have been signed out." nextAction={{ label: 'Go to sign in', onSelect: onDone }} />
      ) : (
        <form className="door-form" onSubmit={onSubmit} aria-label="Complete password reset">
          <label className="door-lbl" htmlFor="reset-new">New password, 14 characters or more</label>
          <Field id="reset-new" name="password" type="password" revealable autoComplete="new-password" value={password} invalid={tooShort} onChange={(event) => setPassword(event.target.value)} required minLength={PASSWORD_MIN_LENGTH} />
          <label className="door-lbl" htmlFor="reset-again">Repeat it</label>
          <Field id="reset-again" name="passwordAgain" type="password" revealable autoComplete="new-password" value={again} invalid={mismatch} onChange={(event) => setAgain(event.target.value)} required />
          {/* The field turned warn with nothing said, on the one screen of the
              four that did not print the count. The other three do. */}
          {tooShort ? <p className="door-note">{PASSWORD_MIN_LENGTH - password.length} more characters needed.</p> : null}
          {mismatch ? <p className="door-note">The two entries differ.</p> : null}
          <div className="door-row">
            <Button variant="primary" type="submit" disabled={complete.isPending}>{complete.isPending ? 'Saving' : 'Set password'}</Button>
            <Button variant="tertiary" onClick={onDone}>Back to sign in</Button>
          </div>
          {complete.isPending ? <HonestState variant="Loading" reason="Waiting for the reset answer (15 s limit)." detail={elapsed} nextAction={{ label: 'Cancel', onSelect: () => complete.reset() }} /> : null}
          {complete.isError ? (
            <HonestState
              variant={rejected ? 'Permission denied' : stateOfError(complete.error).variant}
              title={rejected ? 'Reset link rejected' : 'Reset failed'}
              reason={rejected ? 'The reset token is expired, already used, or malformed. Request a new link.' : stateOfError(complete.error).reason}
              nextAction={[{ label: 'Request a new link', href: '?reset_password=request' }, { label: 'Try again', onSelect: () => complete.reset() }]}
            />
          ) : null}
        </form>
      )}
    </AuthFrame>
  );
}

export function ResetPassword({ param, onDone }: ResetPasswordProps) {
  return isResetRequest(param) ? <RequestForm onDone={onDone} /> : <CompleteForm token={param} onDone={onDone} />;
}
